/**
 * SeoOverrideInjector — mounts once at App root, listens for route
 * changes, and updates <meta name="description">, <link rel="canonical">,
 * and <meta property="og:description"> based on the overrides stored
 * server-side in `db.seo_overrides` (populated by
 * POST /api/admin/site-audit/auto-optimize).
 *
 * This is a client-side hook — Google fully executes JS for indexing,
 * so per-route meta injected here is visible to Googlebot and AI-mode
 * crawlers. A future SSR/edge worker can share the same override map.
 *
 * Never blocks render: the fetch runs after first paint + degrades
 * silently on any failure.
 */
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

const OVERRIDES_TTL_MS = 5 * 60 * 1000; // refetch every 5 minutes at most
let _overridesCache = null;
let _overridesFetchedAt = 0;
let _inflight = null;

async function loadOverrides() {
  const now = Date.now();
  if (_overridesCache && now - _overridesFetchedAt < OVERRIDES_TTL_MS) return _overridesCache;
  if (_inflight) return _inflight;
  const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
  _inflight = fetch(`${API}/api/seo/overrides`)
    .then((r) => (r.ok ? r.json() : { overrides: {} }))
    .then((d) => {
      _overridesCache = d.overrides || {};
      _overridesFetchedAt = Date.now();
      _inflight = null;
      return _overridesCache;
    })
    .catch(() => {
      _inflight = null;
      return {};
    });
  return _inflight;
}

function upsertMeta(name, value) {
  if (!value) return;
  let tag = document.head.querySelector(`meta[name="${name}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute("name", name);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", value);
}

function upsertProperty(prop, value) {
  if (!value) return;
  let tag = document.head.querySelector(`meta[property="${prop}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute("property", prop);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", value);
}

function upsertCanonical(href) {
  if (!href) return;
  let tag = document.head.querySelector('link[rel="canonical"]');
  if (!tag) {
    tag = document.createElement("link");
    tag.setAttribute("rel", "canonical");
    document.head.appendChild(tag);
  }
  tag.setAttribute("href", href);
}

export default function SeoOverrideInjector() {
  const location = useLocation();
  const appliedFor = useRef(null);

  useEffect(() => {
    const path = location.pathname || "/";
    if (appliedFor.current === path) return;
    appliedFor.current = path;
    loadOverrides().then((map) => {
      const override = map[path];
      if (!override) return;
      if (override.meta_description) {
        upsertMeta("description", override.meta_description);
        upsertProperty("og:description", override.meta_description);
        upsertProperty("twitter:description", override.meta_description);
      }
      if (override.canonical_url) upsertCanonical(override.canonical_url);
    });
  }, [location.pathname]);

  return null;
}
